import { useEffect, useState } from 'react';

export default function HeroSection() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleScroll = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      className="relative min-h-[85vh] sm:min-h-[70vh] md:min-h-[100vh] flex flex-col items-center justify-center overflow-hidden py-16 md:py-8"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-center bg-cover"
        style={{
          backgroundImage:
            "url('https://public.readdy.ai/ai/img_res/ecc09d6ac22b03071c52e81ee4e641cc.jpg')",
        }}
      />

      {/* Layered dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-kura-dark/70 via-kura-dark/50 to-kura-dark/80" />
      <div className="absolute inset-0 bg-gradient-to-r from-kura-green/60 via-transparent to-kura-green/40" />

      {/* Ainu-inspired decorative pattern overlay */}
      <div className="absolute inset-0 ainu-pattern opacity-30" />

      {/* Ainu top border */}
      <div className="absolute top-0 left-0 right-0 ainu-border opacity-80" />
      <div className="absolute bottom-0 left-0 right-0 ainu-border opacity-80" />

      {/* Content */}
      <div
        className={`relative z-10 flex flex-col items-center text-center px-4 sm:px-6 md:px-8 w-full max-w-xs sm:max-w-xl md:max-w-3xl lg:max-w-5xl mx-auto transition-all duration-1000 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        {/* Ainu tag */}
        <div
          className={`inline-flex items-center gap-2 mb-3 sm:mb-5 transition-all duration-1000 delay-200 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <span className="h-px w-6 sm:w-10 bg-kura-gold" />
          <span
            className="text-kura-gold-light text-xs tracking-[0.2em] uppercase whitespace-nowrap"
            style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
          >
            アイヌ語で「黄金」を意味する
          </span>
          <span className="h-px w-6 sm:w-10 bg-kura-gold" />
        </div>

        {/* Trade name */}
        <div
          className={`mb-3 sm:mb-4 transition-all duration-1000 delay-300 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <span
            className="gold-shimmer text-2xl sm:text-3xl md:text-5xl font-bold tracking-widest"
            style={{ fontFamily: 'Noto Serif JP, serif' }}
          >
            買取こんかね
          </span>
        </div>

        {/* Catchcopy */}
        <h1
          className={`text-white text-sm sm:text-xl md:text-2xl lg:text-3xl font-light tracking-wide leading-relaxed mb-4 sm:mb-5 transition-all duration-1000 delay-500 whitespace-nowrap ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
          style={{ fontFamily: 'Noto Serif JP, serif' }}
        >
          「どんなものにも、<span className="text-kura-gold-light font-medium">価値</span>がある。」
        </h1>

        {/* Sub description */}
        <p
          className={`text-white/75 text-xs md:text-sm max-w-full sm:max-w-md md:max-w-xl leading-relaxed mb-5 sm:mb-7 tracking-wider transition-all duration-1000 delay-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
          style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
        >
          <span className="block whitespace-nowrap">なんでも買取・催事出張買取専門。</span>
          <span className="block whitespace-nowrap">ブランド品・貴金属・骨董品・家電から衣類まで。</span>
          <span className="block whitespace-nowrap">あなたの大切なものを、正直に、丁寧に査定します。</span>
        </p>

        {/* CTA Buttons */}
        <div
          className={`flex flex-col sm:flex-row gap-3 sm:gap-4 transition-all duration-1000 delay-900 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <button
            onClick={() => handleScroll('#process')}
            className="border border-white/60 hover:border-kura-gold text-white hover:text-kura-gold-light px-6 sm:px-8 py-3 sm:py-3.5 rounded-md tracking-wide transition-all duration-300 cursor-pointer whitespace-nowrap text-sm sm:text-base md:text-lg"
            style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
          >
            買取の流れを見る
          </button>
        </div>

        {/* Features badges */}
        <div
          className={`mt-5 sm:mt-7 flex flex-wrap justify-center gap-3 sm:gap-6 transition-all duration-1000 delay-1000 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {[
            { icon: 'ri-home-2-line', text: '出張査定無料' },
            { icon: 'ri-time-line', text: '即日対応可能' },
            { icon: 'ri-shield-check-line', text: '古物商許可取得済' },
            { icon: 'ri-hand-coin-line', text: '現金即払い' },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-1.5 sm:gap-2 text-white/80">
              <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
                <i className={`${item.icon} text-kura-gold text-base sm:text-xl`} />
              </div>
              <span
                className="text-xs sm:text-sm tracking-wide whitespace-nowrap"
                style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
              >
                {item.text}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-0 right-0 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-white/50 text-xs tracking-widest" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
          SCROLL
        </span>
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-arrow-down-line text-kura-gold text-lg" />
        </div>
      </div>
    </section>
  );
}
